// Phase I4.3 — Daily Acca · Direction A (Intelligence Preview).
// Faithful port of prototype/daily-acca.jsx::DailyAccaA, with the
// hardcoded FREE_ACCA / FOUNDERS_PICK constants replaced by props
// fetched from useFreeTierToday(). Empty/loading states use the
// shared cards in EmptyStates.tsx.

import { useState } from "react";
import { ConfMeter, Eyebrow, Badge } from "../../../components/super-acca";
import type { FoundersPick, FreeAcca } from "../../../free-tier/useFreeTierToday.ts";
import { summarizeReasoning } from "../../../agents/reasoning.ts";
import { EmptyAccaCard, EmptyFounderCard, LoadingCard } from "./EmptyStates.tsx";
import { legToVM } from "./legViewModel.ts";
import { marketLabel, selectionLabel } from "../../../exchange/market-format.ts";

export interface DailyAccaDirectionProps {
  acca: FreeAcca | null;
  founder: FoundersPick | null;
  recentAccaResult: FreeAcca | null;
  isPro: boolean;
  loading: boolean;
}

const fontDisplay = "var(--sa-font-display)";
const fontMono = "var(--sa-font-mono)";
const fontBody = "var(--sa-font-body)";
const fontStat = "var(--sa-font-stat)";

export function DailyAccaA({ acca, founder, isPro, loading }: DailyAccaDirectionProps): JSX.Element {
  const [openLeg, setOpenLeg] = useState(0);

  return (
    <div style={{ padding: "36px 48px 72px", maxWidth: 1180, margin: "0 auto" }}>
      <div
        style={{
          fontFamily: fontMono,
          fontSize: 10.5,
          letterSpacing: "0.22em",
          color: "var(--sa-fg-muted)",
          textTransform: "uppercase",
        }}
      >
        STRIKZA · {isPro ? "PRO" : "FREE TIER"}
      </div>
      <h1
        style={{
          fontFamily: fontDisplay,
          fontSize: 56,
          lineHeight: 1.04,
          fontWeight: 700,
          margin: "14px 0 10px",
          letterSpacing: "-0.01em",
        }}
      >
        Daily Acca
      </h1>
      <p style={{ color: "var(--sa-fg-secondary)", fontSize: 16, maxWidth: 720, lineHeight: 1.6 }}>
        Today's free {isPro ? "5-leg" : "3-leg"} accumulator + Founder's Pick.{" "}
        {isPro ? "Pro tier — deeper slate." : "Full reasoning unlocked on Pro."}
      </p>

      {loading ? (
        <div style={{ marginTop: 32 }}><LoadingCard /></div>
      ) : acca ? (
        <AccaPanel acca={acca} isPro={isPro} openLeg={openLeg} setOpenLeg={setOpenLeg} />
      ) : (
        <div style={{ marginTop: 32 }}><EmptyAccaCard /></div>
      )}

      {!loading && (founder ? <FounderPanel founder={founder} /> : <div style={{ marginTop: 32 }}><EmptyFounderCard /></div>)}
    </div>
  );
}

function AccaPanel({
  acca,
  isPro,
  openLeg,
  setOpenLeg,
}: {
  acca: FreeAcca;
  isPro: boolean;
  openLeg: number;
  setOpenLeg: (n: number) => void;
}): JSX.Element {
  const stake = 100;
  const target = Math.round(stake * (acca.combined_odds || 1));
  return (
    <div
      style={{
        marginTop: 32,
        border: "1px solid var(--sa-hairline)",
        background: "var(--sa-bg-panel)",
        borderRadius: 8,
        padding: 28,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22 }}>
        <div>
          <Eyebrow>Today's Free Acca</Eyebrow>
          <div style={{ fontFamily: fontDisplay, fontSize: 28, fontWeight: 600, marginTop: 12 }}>
            {acca.leg_count}-Leg Accumulator
          </div>
          <div style={{ fontFamily: fontBody, fontSize: 13, color: "var(--sa-fg-muted)", marginTop: 4 }}>
            {acca.acca_date} · Published 11:30 GMT
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: fontStat, fontSize: 56, lineHeight: 0.9, color: "var(--sa-accent-saffron)" }}>
            {acca.combined_odds.toFixed(2)}×
          </div>
          <div style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.18em", color: "var(--sa-fg-muted)", marginTop: 6 }}>
            COMBINED ODDS
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <ConfMeter
          value={acca.confidence ?? 0}
          locked={!isPro}
          label={isPro ? "Confidence" : "70+ confidence · unlock with Pro"}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {acca.legs.map((rawLeg, i) => {
          const leg = legToVM(rawLeg, i, acca.confidence);
          return (
            <div
              key={`${rawLeg.fixture_id}|${i}`}
              onClick={() => setOpenLeg(openLeg === i ? -1 : i)}
              style={{ padding: "18px 0", borderTop: "1px solid var(--sa-hairline)", cursor: "pointer" }}
            >
              <div style={{ display: "grid", gridTemplateColumns: "40px 1fr 160px 100px 60px", gap: 16, alignItems: "center" }}>
                <span style={{ fontFamily: fontMono, fontSize: 10, letterSpacing: "0.18em", color: "var(--sa-fg-muted)" }}>
                  L0{i + 1}
                </span>
                <div>
                  <div style={{ fontFamily: fontBody, fontSize: 15, color: "var(--sa-fg-primary)", fontWeight: 500 }}>
                    {leg.home} <span style={{ color: "var(--sa-fg-muted)" }}>vs</span> {leg.away}
                  </div>
                  <div style={{ fontFamily: fontMono, fontSize: 10.5, color: "var(--sa-fg-muted)", letterSpacing: "0.12em", marginTop: 4 }}>
                    {leg.league} · {leg.kickoff}
                  </div>
                </div>
                <div>
                  <div style={{ fontFamily: fontBody, fontSize: 13, color: "var(--sa-fg-primary)" }}>{leg.market}</div>
                  <div style={{ fontFamily: fontMono, fontSize: 11, color: "var(--sa-accent-saffron)", marginTop: 3 }}>{leg.selection}</div>
                </div>
                <div style={{ fontFamily: fontMono, fontSize: 16, color: "var(--sa-fg-primary)", textAlign: "right" }}>
                  {leg.odds.toFixed(2)}×
                </div>
                <span style={{ color: "var(--sa-fg-muted)", fontSize: 14, textAlign: "right" }}>
                  {openLeg === i ? "−" : "+"}
                </span>
              </div>
              {openLeg === i ? (
                <div
                  style={{
                    marginTop: 14,
                    padding: "14px 16px",
                    background: "var(--sa-bg-raised)",
                    borderLeft: "2px solid var(--sa-amber-dim)",
                    fontFamily: fontBody,
                    fontSize: 13.5,
                    color: "var(--sa-fg-secondary)",
                    lineHeight: 1.6,
                  }}
                >
                  {leg.reasoning}
                  {!isPro ? (
                    <div style={{ marginTop: 8, fontFamily: fontMono, fontSize: 10.5, color: "var(--sa-accent-saffron)", letterSpacing: "0.16em" }}>
                      + 1 MORE SENTENCE OF REASONING ON PRO →
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 22,
          paddingTop: 18,
          borderTop: "1px solid var(--sa-hairline)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontFamily: fontMono, fontSize: 12, color: "var(--sa-fg-secondary)" }}>
          GH₵{stake} returns <span style={{ color: "var(--sa-accent-saffron)" }}>GH₵{target.toLocaleString()}</span>
        </div>
        <span style={{ fontFamily: fontBody, fontSize: 12, color: "var(--sa-fg-muted)" }}>
          Tap any leg to read reasoning
        </span>
      </div>
    </div>
  );
}

function FounderPanel({ founder }: { founder: FoundersPick }): JSX.Element {
  const reasoning = summarizeReasoning(founder.reasoning) ?? "";
  return (
    <div
      style={{
        marginTop: 32,
        position: "relative",
        border: "1px solid var(--sa-amber-dim)",
        background: "linear-gradient(180deg, #14110A 0%, var(--sa-bg-raised) 100%)",
        borderRadius: 8,
        padding: 32,
        overflow: "hidden",
      }}
    >
      <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40 }}>
        <div>
          <Eyebrow>Founder's Pick · Today</Eyebrow>
          <div style={{ fontFamily: fontMono, fontSize: 11, letterSpacing: "0.16em", color: "var(--sa-fg-muted)", marginTop: 14 }}>
            {founder.fixture_id.toUpperCase()} · {founder.pick_date}
          </div>
          <div style={{ fontFamily: fontDisplay, fontSize: 36, fontWeight: 700, lineHeight: 1.1, marginTop: 14, color: "var(--sa-fg-primary)" }}>
            {marketLabel(founder.market)}: {selectionLabel(founder.market, founder.selection)}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18 }}>
            <span style={{ fontFamily: fontStat, fontSize: 48, lineHeight: 0.9, color: "var(--sa-accent-saffron)" }}>
              {founder.odds.toFixed(2)}×
            </span>
            <Badge variant="amber">FOUNDER'S PICK</Badge>
          </div>
        </div>
        <div style={{ borderLeft: "1px solid var(--sa-hairline-bright)", paddingLeft: 32 }}>
          <div style={{ fontFamily: fontMono, fontSize: 10.5, color: "var(--sa-accent-saffron)", letterSpacing: "0.18em", marginBottom: 12 }}>
            WHY THIS PICK
          </div>
          <p style={{ fontFamily: fontBody, fontSize: 14, color: "var(--sa-fg-secondary)", lineHeight: 1.7 }}>
            {reasoning || "Highest-conviction submission from THE DESK today."}
          </p>
        </div>
      </div>
    </div>
  );
}
